import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useVendorAuth } from './VendorAuthProvider';
import { Building, Shield, Loader2, AlertCircle, Mail, Lock, X, CheckCircle } from 'lucide-react';

const VendorLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loading, resetPassword } = useVendorAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate('/vendor/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);
    setIsResetting(true);

    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
      // Auto-close modal after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (err: any) {
      setResetError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sunbeth-orange-50 to-sunbeth-orange-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 flex items-center justify-center">
            <img 
              src="/logo192.png" 
              alt="Sunbeth Global Concepts" 
              className="h-20 w-20 object-contain"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Vendor Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access your vendor account
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-left text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-sunbeth-orange-500 focus:border-sunbeth-orange-500"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-left text-sm font-medium text-gray-700">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-500 focus:outline-none"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-sunbeth-orange-500 focus:border-sunbeth-orange-500"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isSubmitting || loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-sunbeth-orange-600 hover:bg-sunbeth-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sunbeth-orange-500'
                } transition duration-150 ease-in-out`}
              >
                {isSubmitting || loading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Sign in
                  </>
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/vendor/register"
                  className="font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-500"
                >
                  Register here
                </Link>
              </p>
            </div>
          </form>

          {/* Security Notice */}
          <div className="mt-6 bg-sunbeth-orange-50 border border-sunbeth-orange-200 rounded-md p-4">
            <div className="flex">
              <Shield className="h-5 w-5 text-sunbeth-orange-400 mr-2 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-sunbeth-orange-800">Secure Access</h4>
                <p className="text-sm text-sunbeth-orange-700 mt-1">
                  Your vendor data is securely stored and only accessible to you.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 Sunbeth Vendor Management System. All rights reserved.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            {/* Close Button */}
            <button
              onClick={closeForgotPasswordModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Modal Header */}
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Reset Password</h3>
              <p className="mt-2 text-sm text-gray-600">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {/* Success Message */}
            {resetSuccess && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
                <div className="flex">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                  <div>
                    <p className="text-sm text-green-700 font-medium">Email sent successfully!</p>
                    <p className="text-sm text-green-600 mt-1">
                      Please check your inbox for password reset instructions.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {resetError && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-700">{resetError}</p>
                </div>
              </div>
            )}

            {/* Forgot Password Form */}
            {!resetSuccess && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="block text-left text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="reset-email"
                      name="reset-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-sunbeth-orange-500 focus:border-sunbeth-orange-500"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sunbeth-orange-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isResetting}
                    className={`flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                      isResetting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-sunbeth-orange-600 hover:bg-sunbeth-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sunbeth-orange-500'
                    } transition duration-150 ease-in-out`}
                  >
                    {isResetting ? (
                      <>
                        <Loader2 className="animate-spin inline-block -ml-1 mr-2 h-4 w-4" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Success State - Close Button */}
            {resetSuccess && (
              <button
                onClick={closeForgotPasswordModal}
                className="w-full mt-4 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-sunbeth-orange-600 hover:bg-sunbeth-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sunbeth-orange-500 transition duration-150 ease-in-out"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLoginPage;

